import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Grid
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  NotificationsActive as PushIcon,
  PhoneAndroid as PhoneIcon,
  Computer as ComputerIcon,
  Tablet as TabletIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

const PushNotificationSetup = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [permission, setPermission] = useState('default');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const steps = ['Check Browser Support', 'Request Permission', 'Subscribe', 'Test Notification'];

  useEffect(() => {
    checkBrowserSupport();
    checkExistingSubscription();
  }, []);

  // Check if browser supports push notifications
  const checkBrowserSupport = () => {
    if (!('serviceWorker' in navigator)) {
      setError('Service Workers are not supported in your browser');
      return false;
    }

    if (!('PushManager' in window)) {
      setError('Push notifications are not supported in your browser');
      return false;
    }

    if (!('Notification' in window)) {
      setError('Notifications are not supported in your browser');
      return false;
    }

    setActiveStep(1);
    return true;
  };

  // Check existing subscription
  const checkExistingSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription = await registration.pushManager.getSubscription();
      
      if (existingSubscription) {
        setSubscription(existingSubscription);
        setPermission(Notification.permission);
        if (Notification.permission === 'granted') {
          setActiveStep(3);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  // Request notification permission
  const requestPermission = async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted') {
        setActiveStep(2);
        Swal.fire('Success', 'Permission granted! You can now subscribe to push notifications.', 'success');
      } else if (result === 'denied') {
        setError('Permission denied. Please enable notifications in your browser settings.');
        Swal.fire('Permission Denied', 'You have blocked notifications. Please enable them in your browser settings.', 'error');
      }
    } catch (error) {
      setError('Error requesting permission: ' + error.message);
    }
  };

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    setLoading(true);
    try {
      // Get VAPID public key from server
      const configResponse = await axios.get('/api/notification-config/vapid-key');
      const vapidPublicKey = configResponse.data.publicKey;

      if (!vapidPublicKey) {
        throw new Error('VAPID public key not configured on server');
      }

      // Register service worker
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      await navigator.serviceWorker.ready;

      // Subscribe to push
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Save subscription to server
      const response = await axios.post('/api/notification-preferences/push-subscription', {
        subscription: pushSubscription.toJSON()
      });

      if (response.data.success) {
        setSubscription(pushSubscription);
        setActiveStep(3);
        Swal.fire('Success', 'Successfully subscribed to push notifications!', 'success');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setError('Failed to subscribe: ' + error.message);
      Swal.fire('Error', 'Failed to subscribe to push notifications', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Unsubscribe from push notifications
  const unsubscribe = async () => {
    try {
      if (subscription) {
        await subscription.unsubscribe();
        await axios.post('/api/notification-preferences/push-unsubscribe');
        setSubscription(null);
        setActiveStep(1);
        Swal.fire('Success', 'Unsubscribed from push notifications', 'success');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to unsubscribe', 'error');
    }
  };

  // Send test notification
  const sendTestNotification = async () => {
    try {
      const response = await axios.post('/api/notification-preferences/test-push');
      if (response.data.success) {
        Swal.fire('Success', 'Test notification sent! Check your browser.', 'success');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to send test notification', 'error');
    }
  };

  // Helper function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Get device icon
  const getDeviceIcon = () => {
    const ua = navigator.userAgent;
    if (/mobile/i.test(ua)) return <PhoneIcon />;
    if (/tablet/i.test(ua)) return <TabletIcon />;
    return <ComputerIcon />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Push Notification Setup
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Enable push notifications to receive real-time alerts on this device
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stepper */}
      <Paper sx={{ p: 3, mt: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {/* Step 0: Browser Support */}
          {activeStep === 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Checking Browser Support...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Verifying if your browser supports push notifications
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Step 1: Request Permission */}
          {activeStep === 1 && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <PushIcon color="primary" fontSize="large" />
                  <Typography variant="h6">
                    Enable Push Notifications
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  To receive push notifications, we need your permission. Click the button below to grant permission.
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  You can change this permission anytime in your browser settings
                </Alert>
                <Button
                  variant="contained"
                  size="large"
                  onClick={requestPermission}
                  disabled={permission === 'granted'}
                >
                  {permission === 'granted' ? 'Permission Granted' : 'Request Permission'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Subscribe */}
          {activeStep === 2 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Subscribe to Push Notifications
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Click subscribe to start receiving push notifications on this device
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Real-time Alerts"
                      secondary="Get notified instantly about important updates"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Work Offline"
                      secondary="Receive notifications even when the app is closed"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Customizable"
                      secondary="Control which notifications you receive"
                    />
                  </ListItem>
                </List>
                <Button
                  variant="contained"
                  size="large"
                  onClick={subscribeToPush}
                  disabled={loading || subscription !== null}
                  sx={{ mt: 2 }}
                >
                  {loading ? 'Subscribing...' : subscription ? 'Already Subscribed' : 'Subscribe Now'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Test */}
          {activeStep === 3 && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <CheckIcon color="success" fontSize="large" />
                  <Typography variant="h6">
                    Push Notifications Enabled!
                  </Typography>
                </Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  You are now subscribed to push notifications on this device
                </Alert>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Test your push notifications to make sure everything is working correctly
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={sendTestNotification}
                  >
                    Send Test Notification
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={unsubscribe}
                  >
                    Unsubscribe
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Status
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    {permission === 'granted' ? <CheckIcon color="success" /> : <ErrorIcon color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary="Permission"
                    secondary={permission === 'granted' ? 'Granted' : permission === 'denied' ? 'Denied' : 'Not Requested'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {subscription ? <CheckIcon color="success" /> : <ErrorIcon color="disabled" />}
                  </ListItemIcon>
                  <ListItemText
                    primary="Subscription"
                    secondary={subscription ? 'Active' : 'Not Subscribed'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {getDeviceIcon()}
                  </ListItemIcon>
                  <ListItemText
                    primary="Device"
                    secondary={navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Browser Support
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip
                  label="Chrome"
                  color="success"
                  size="small"
                  icon={<CheckIcon />}
                />
                <Chip
                  label="Firefox"
                  color="success"
                  size="small"
                  icon={<CheckIcon />}
                />
                <Chip
                  label="Edge"
                  color="success"
                  size="small"
                  icon={<CheckIcon />}
                />
                <Chip
                  label="Safari (macOS 16.1+)"
                  color="warning"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PushNotificationSetup;
